import { useMemo, useState } from 'react';

import { useStoreContext } from 'features/store';

import { ListCardGroup } from 'components/list-card-group';
import { ProductCard } from 'components/product-card';
import { useMenuCalories } from 'hooks/menu/use-menu-calories';
import { ClickEventComponentNames, logButtonClick } from 'state/amplitude';
import { useMainMenuContext } from 'state/menu/main-menu';
import { ILocalizedMenuEntry } from 'state/unified-menu/types';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import { getEntryPrice, stringifySanityImage } from 'state/unified-menu/utils';

import { useProductWizardContext } from '../../product-wizard';
import { DEFAULT_SLOT_KEY } from '../../product-wizard/types';
import { useProductWizardFlow } from '../../product-wizard/use-product-wizard-flow';
import { SelectorContainer } from '../styled';

import { ProductCustomizationModifiers } from './product-customization-modifiers';
import { CustomizationRenderItem } from './types';
import { getComboSlotItems } from './utils';

export const ProductCustomizationItem = () => {
  const { indexedMenu } = useUnifiedMenuContext();
  const { currentFlowStep } = useProductWizardFlow();
  const { slotsSelections, selectComboSlotItem } = useProductWizardContext();
  const menuQueryOptions = useMenuQueryOptions();
  const { formatCalories } = useMenuCalories();

  const { store } = useStoreContext();
  const { activeDayParts } = useMainMenuContext();
  const storeHasBurgersForBreakfast = Boolean(store.hasBurgersForBreakfast);

  const slotKey = currentFlowStep.slotKey!;
  const selectedSlot = slotsSelections[slotKey];

  const [selectedRow, setSelectedRow] = useState<number>();

  const comboSlotItems: ILocalizedMenuEntry[] = useMemo(
    () =>
      slotKey === DEFAULT_SLOT_KEY
        ? []
        : getComboSlotItems({
            menu: indexedMenu,
            slotKey,
            posServiceMode: menuQueryOptions.posServiceMode,
            activeDayParts,
            storeHasBurgersForBreakfast,
          }),
    [
      slotKey,
      indexedMenu,
      menuQueryOptions.posServiceMode,
      activeDayParts,
      storeHasBurgersForBreakfast,
    ]
  );

  return (
    <>
      {/* If it has less than 2 entries, we render the modifiers component. */}
      {comboSlotItems.length < 2 ? null : (
        <SelectorContainer>
          <ListCardGroup
            items={comboSlotItems}
            selectedRow={selectedRow}
            renderItem={({ item, index, rowIndex }: CustomizationRenderItem) => (
              <ProductCard
                key={`item-${item.id}-${index}`}
                isSelected={item.id === selectedSlot?.itemId}
                productProps={{
                  name: item.name?.locale,
                  price: getEntryPrice(item, menuQueryOptions.posServiceMode),
                  calories: formatCalories(item.nutrition?.calories?.def),
                  image: stringifySanityImage(item.image),
                }}
                onProductPress={() => {
                  logButtonClick({
                    attributes: {
                      component: ClickEventComponentNames.PRODUCT_COMBO_SLOT_SELECTION_UPDATE,
                      name: item.name.locale ?? '',
                      Id: item.id ?? '',
                    },
                  });
                  selectComboSlotItem(slotKey, item.id);
                  setSelectedRow(rowIndex);
                }}
              />
            )}
          />
        </SelectorContainer>
      )}
      {/* Render Modifiers if applicable */}
      <ProductCustomizationModifiers />
    </>
  );
};
